import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  Label,
  FormFeedback,
  Form,
  Collapse,
  Button,
  Alert,
  UncontrolledTooltip,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Dropzone from "react-dropzone";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RiCloseLine, RiCpuLine, RiHardDriveLine, RiPlugLine, RiServerLine } from "react-icons/ri";
import { MdOutlineMemory } from "react-icons/md";
import api, { handleApiError } from "../../../helpers/api";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { useTranslation } from 'react-i18next';

const EcommerceAddProduct = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const isUpdateMode = !!id;
  document.title = isUpdateMode ? t("ecommerce_add_product.title_update") : t("ecommerce_add_product.title_create");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [specDefinitions, setSpecDefinitions] = useState([]);
  const [possibleSpecs, setPossibleSpecs] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isCustomizable, setIsCustomizable] = useState(false);
  const [usedSpecs, setUsedSpecs] = useState([]);
  const [specConstraints, setSpecConstraints] = useState({});
  const [newSpec, setNewSpec] = useState(null);
  const [newOption, setNewOption] = useState({});
  const [newSpecError, setNewSpecError] = useState(null);
  const [newOptionErrors, setNewOptionErrors] = useState({});
  const [openPanels, setOpenPanels] = useState({});
  const [simpleSpecs, setSimpleSpecs] = useState([{ key: "", value: "", isNewSpec: false, isNewValue: false }]);
  const [newProductOption, setNewProductOption] = useState({});

  const validation = useFormik({
    initialValues: {
      name: "",
      brand: "",
      stock: "",
      price: "",
      description: "",
      category: "",
    },
    validationSchema: Yup.object({
     name: Yup.string().required("Name is required"),
      brand: Yup.string().required("Brand is required"),
      stock: Yup.number().min(0, "Stock must be a non-negative number").required("Stock is required"),
      price: Yup.number().min(0, "Price must be a non-negative number").required("Price is required"),
      description: Yup.string().required("Description is required"),
      category: Yup.string().required("Category is required"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("brand", values.brand);
        formData.append("stock", values.stock);
        formData.append("price", values.price);
        formData.append("description", values.description);
        formData.append("category", values.category);
        formData.append("customizable", isCustomizable);

        if (isCustomizable) {
          const specs = {};
          usedSpecs.forEach((spec) => {
            specs[spec.name] = spec.options.map((opt) => ({ name: opt.name, included: opt.included }));
          });
          formData.append("specs", JSON.stringify(specs));
          formData.append("usedSpecs", JSON.stringify(usedSpecs));
          formData.append("specConstraints", JSON.stringify(specConstraints));
          if (pdfFile && pdfFile.file) {
            formData.append("pdfGuide", pdfFile.file);
          }
        } else {
          const specs = simpleSpecs.reduce((acc, spec) => {
            if (spec.key && spec.value) acc[spec.key] = spec.value;
            return acc;
          }, {});
          if (Object.keys(specs).length === 0) {
            toast.error(t("ecommerce_add_product.toasts.spec_required"), { position: "top-right", autoClose: 3000 });
            setIsSubmitting(false);
            return;
          }
          formData.append("specs", JSON.stringify(specs));
          const updatedPossibleSpecs = [...possibleSpecs];
          simpleSpecs.forEach((spec) => {
            if (spec.isNewSpec && spec.key) {
              const existingSpec = updatedPossibleSpecs.find((ps) => ps.name === spec.key);
              if (!existingSpec) {
                updatedPossibleSpecs.push({ name: spec.key, options: [{ value: spec.value, count: 1 }] });
              } else if (spec.isNewValue && spec.value && !existingSpec.options.some((opt) => opt.value === spec.value)) {
                existingSpec.options.push({ value: spec.value, count: 1 });
              }
            } else if (spec.isNewValue && spec.value) {
              const existingSpec = updatedPossibleSpecs.find((ps) => ps.name === spec.key);
              if (existingSpec && !existingSpec.options.some((opt) => opt.value === spec.value)) {
                existingSpec.options.push({ value: spec.value, count: 1 });
              }
            }
          });
          formData.append("possibleSpecs", JSON.stringify(updatedPossibleSpecs));
        }

        const newFiles = selectedFiles.filter(file => !file.isExisting);
        newFiles.forEach((file) => {
          formData.append("images", file);
        });

        const existingImages = selectedFiles
            .filter(file => file.isExisting)
            .map(file => ({ url: file.preview, public_id: file.public_id }));
        if (existingImages.length > 0) {
          formData.append("existingImages", JSON.stringify(existingImages));
        }

        const endpoint = isUpdateMode ? `/products/${id}` : "/products";
        const method = isUpdateMode ? api.patch : api.post;

        await method(endpoint, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(t(isUpdateMode ? "ecommerce_add_product.toasts.update_success" : "ecommerce_add_product.toasts.create_success"), {
          position: "top-right",
          autoClose: 3000,
        });
        navigate("/apps-ecommerce-products");
        if (!isUpdateMode) {
          validation.resetForm();
          setUsedSpecs([]);
          setSpecConstraints({});
          setSelectedFiles([]);
          setPdfFile(null);
          setIsCustomizable(false);
          setSimpleSpecs([{ key: "", value: "", isNewSpec: false, isNewValue: false }]);
          setNewProductOption({});
        }
      } catch (error) {
        handleApiError(error, dispatch, navigate);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    api
        .get("/categories")
        .then((response) => setCategories(response.data.data || []))
        .catch((error) => handleApiError(error, dispatch, navigate));

    if (isUpdateMode) {
      api
          .get(`/products/${id}`)
          .then((response) => {
            const product = response.data.data;
            validation.setValues({
              name: product.name || "",
              brand: product.brand || "",
              stock: product.stock || "",
              price: product.price || "",
              description: product.description || "",
              category: product.category?._id || "",
            });
            setIsCustomizable(product.customizable || false);
            setSelectedFiles(
                product.images?.map((img, index) => ({
                  preview: img.url,
                  name: `Image-${index + 1}`,
                  formattedSize: "N/A",
                  public_id: img.public_id,
                  isExisting: true,
                })) || []
            );
            if (product.customizable && product.pdfGuide) {
              setPdfFile({
                file: null,
                preview: product.pdfGuide.url,
                name: product.pdfGuide.name || "Guide.pdf",
                formattedSize: "N/A",
                public_id: product.pdfGuide.public_id,
                isExisting: true,
              });
            }
            if (product.customizable) {
              setUsedSpecs(product.usedSpecs || []);
              setSpecConstraints(product.specConstraints || {});
            } else {
              setSimpleSpecs(
                  Object.entries(product.specs || {}).map(([key, value]) => ({
                    key,
                    value,
                    isNewSpec: !possibleSpecs.some((ps) => ps.name === key),
                    isNewValue: !possibleSpecs.some((ps) => ps.name === key && ps.options?.some((opt) => opt.value === value)),
                  })) || [{ key: "", value: "", isNewSpec: false, isNewValue: false }]
              );
            }
          })
          .catch((error) => handleApiError(error, dispatch, navigate));
    }
  }, [id, isUpdateMode, dispatch, navigate]);

  useEffect(() => {
    if (validation.values.category) {
      api
          .get(`/categories/${validation.values.category}`)
          .then((response) => {
            const category = response.data.data;
            const parentCategoryId = category.parent?._id;
            setPossibleSpecs(category.possibleSpecs || []);
            if (isCustomizable) {
              if (!category.customizable) {
                toast.warn(t("ecommerce_add_product.toasts.category_not_customizable"), {
                  position: "top-right",
                  autoClose: 3000,
                });
                setIsCustomizable(false);
                setSpecDefinitions([]);
                setPdfFile(null);
              } else {
                api
                    .get(`/specDefinitions?category=${validation.values.category}`)
                    .then(async (response) => {
                      if (response.data.data.length === 0) {
                        const parentResponse = await api.get(`/specDefinitions?category=${parentCategoryId}`);
                        setSpecDefinitions(parentResponse.data.data || []);
                      } else {
                        setSpecDefinitions(response.data.data);
                      }
                    })
                    .catch((error) => handleApiError(error, dispatch, navigate));
              }
            }
          })
          .catch((error) => handleApiError(error, dispatch, navigate));
    } else {
      setSpecDefinitions([]);
      setPossibleSpecs([]);
      setPdfFile(null);
    }
  }, [isCustomizable, validation.values.category, dispatch, navigate, t]);

  const handleAcceptedFiles = async (files) => {
    if (selectedFiles.length + files.length > 5) {
      toast.error(t("ecommerce_add_product.toasts.max_images"), { position: "top-right", autoClose: 3000 });
      return;
    }
    const compressedFiles = await Promise.all(
        files.map(async (file) => {
          if (file.type.startsWith('image/')) {
            try {
              const compressedFile = await imageCompression(file, {
                maxSizeMB: 1,
                maxWidthOrHeight: 1200,
                useWebWorker: true,
              });
              return Object.assign(compressedFile, {
                preview: URL.createObjectURL(compressedFile),
                formattedSize: formatBytes(compressedFile.size),
                isExisting: false,
              });
            } catch (error) {
              console.error('Image compression error:', error);
              return Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
                isExisting: false,
              });
            }
          }
          return Object.assign(file, {
            preview: URL.createObjectURL(file),
            formattedSize: formatBytes(file.size),
            isExisting: false,
          });
        })
    );
    setSelectedFiles([...selectedFiles, ...compressedFiles]);
  };

  const handleAcceptedPdf = (files) => {
    if (files.length > 1) {
      toast.error(t("ecommerce_add_product.toasts.one_pdf"), { position: "top-right", autoClose: 3000 });
      return;
    }
    const file = files[0];
    if (file.type !== "application/pdf") {
      toast.error(t("ecommerce_add_product.toasts.pdf_only"), { position: "top-right", autoClose: 3000 });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("ecommerce_add_product.toasts.pdf_size"), { position: "top-right", autoClose: 3000 });
      return;
    }
    setPdfFile({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      formattedSize: formatBytes(file.size),
      isExisting: false,
    });
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return t("ecommerce_add_product.file_size.zero");
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = [
      t("ecommerce_add_product.file_size.bytes"),
      t("ecommerce_add_product.file_size.kb"),
      t("ecommerce_add_product.file_size.mb"),
      t("ecommerce_add_product.file_size.gb"),
      t("ecommerce_add_product.file_size.tb")
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const deleteImage = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const deletePdf = () => {
    setPdfFile(null);
  };

  const addSpec = (spec) => {
    if (!usedSpecs.some((s) => s.name === spec.name)) {
      setUsedSpecs([...usedSpecs, {
        name: spec.name,
        options: spec.options.map((o) => ({ name: o.name, included: false }))
      }]);
    }
  };

  const removeOption = (specName, optionName) => {
    setUsedSpecs(
        usedSpecs
            .map((spec) =>
                spec.name === specName
                    ? { ...spec, options: spec.options.filter((opt) => opt.name !== optionName) }
                    : spec
            )
            .filter((spec) => spec.options.length > 0)
    );
  };

  const removeSpec = (specName) => {
    setUsedSpecs(usedSpecs.filter((spec) => spec.name !== specName));
    setSpecConstraints((prev) => {
      const updated = { ...prev };
      delete updated[specName];
      return updated;
    });
  };

  const updateConstraint = (specName, field, value) => {
    setSpecConstraints((prev) => {
      const updated = { ...prev };
      if (!updated[specName]) updated[specName] = {};
      if (value === undefined || value === "") {
        delete updated[specName][field];
        if (Object.keys(updated[specName]).length === 0) delete updated[specName];
      } else {
        updated[specName][field] = Number(value);
      }
      return updated;
    });
  };

  const toggleIncluded = (specName, optionName) => {
    setUsedSpecs(
        usedSpecs.map((spec) =>
            spec.name === specName
                ? {
                  ...spec,
                  options: spec.options.map((opt) =>
                      opt.name === optionName ? { ...opt, included: !opt.included } : opt
                  ),
                }
                : spec
        )
    );
  };
  const filteredSpecs = specDefinitions.filter((spec) =>
      spec.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const togglePanel = (specName) => {
    setOpenPanels({ ...openPanels, [specName]: !openPanels[specName] });
  };

  const addNewSpec = () => {
    setNewSpec({ name: "", multiple: false, max: 1, options: [], basePrice: null });
    setNewSpecError(null);
  };

  const cancelNewSpec = () => {
    setNewSpec(null);
    setNewSpecError(null);
  };

  const addNewOption = (specName) => {
    setNewOption({ ...newOption, [specName]: { name: "", price: 0 } });
    setNewOptionErrors({ ...newOptionErrors, [specName]: null });
  };

  const cancelNewOption = (specName) => {
    setNewOption({ ...newOption, [specName]: null });
    setNewOptionErrors({ ...newOptionErrors, [specName]: null });
  };

  const saveNewSpec = async (categoryId) => {
    if (!newSpec.name || (newSpec.options.length === 0 && !newSpec.basePrice && !newSpec.multiple)) {
      setNewSpecError(t("ecommerce_add_product.toasts.new_spec_error"));
      return;
    }
    try {
      const payload = { ...newSpec, category: categoryId };
      if (newSpec.multiple) {
        delete payload.basePrice;
      }
      const response = await api.post("/specDefinitions", payload);
      setSpecDefinitions([...specDefinitions, response.data.data]);
      setPossibleSpecs([...possibleSpecs, {
        name: response.data.data.name,
        options: response.data.data.options.map(opt => ({ value: opt.name, count: 1 }))
      }]);
      setNewSpec(null);
      setNewSpecError(null);
      toast.success(t("ecommerce_add_product.toasts.spec_added"), { position: "top-right", autoClose: 3000 });
    } catch (error) {
      handleApiError(error, dispatch, navigate);
    }
  };

  const saveNewOption = async (specId, specName) => {
    if (!newOption[specName]?.name) {
      setNewOptionErrors({ ...newOptionErrors, [specName]: t("ecommerce_add_product.toasts.new_option_error") });
      return;
    }
    try {
      const spec = specDefinitions.find((s) => s.name === specName);
      const updatedOptions = [...spec.options, { ...newOption[specName], price: newOption[specName].price || 0 }];
      const response = await api.patch(`/specDefinitions/${specId}`, { options: updatedOptions });
      setSpecDefinitions(
          specDefinitions.map((s) => (s._id === specId ? response.data.data : s))
      );
      setPossibleSpecs(
          possibleSpecs.map((ps) =>
              ps.name === specName
                  ? { ...ps, options: updatedOptions.map(opt => ({ value: opt.name, count: 1 })) }
                  : ps
          )
      );
      setNewOption({ ...newOption, [specName]: null });
      setNewOptionErrors({ ...newOptionErrors, [specName]: null });
      toast.success(t("ecommerce_add_product.toasts.option_added"), { position: "top-right", autoClose: 3000 });
    } catch (error) {
      handleApiError(error, dispatch, navigate);
    }
  };

  const addProductOption = (specName) => {
    setNewProductOption({ ...newProductOption, [specName]: { name: "", price: 0 } });
  };

  const cancelProductOption = (specName) => {
    setNewProductOption({ ...newProductOption, [specName]: null });
  };

  const saveProductOption = (specName) => {
    if (!newProductOption[specName]?.name) {
      toast.error(t("ecommerce_add_product.toasts.new_option_error"), { position: "top-right", autoClose: 3000 });
      return;
    }
    setUsedSpecs(
        usedSpecs.map((spec) =>
            spec.name === specName
                ? {
                  ...spec,
                  options: [
                    ...spec.options,
                    { name: newProductOption[specName].name, included: false, price: newProductOption[specName].price || 0 }
                  ]
                }
                : spec
        )
    );
    setNewProductOption({ ...newProductOption, [specName]: null });
    toast.success(t("ecommerce_add_product.toasts.product_option_added"), { position: "top-right", autoClose: 3000 });
  };

  const addSimpleSpec = () => {
    setSimpleSpecs([...simpleSpecs, { key: "", value: "", isNewSpec: false, isNewValue: false }]);
  };

  const updateSimpleSpec = (index, field, value) => {
    const updatedSpecs = [...simpleSpecs];
    if (field === "isNewSpec" || field === "isNewValue") {
      updatedSpecs[index][field] = value;
      if (field === "isNewSpec" && !value) {
        updatedSpecs[index].key = "";
        updatedSpecs[index].value = "";
        updatedSpecs[index].isNewValue = false;
      }
      if (field === "isNewValue" && !value) {
        updatedSpecs[index].value = "";
      }
    } else {
      updatedSpecs[index][field] = value;
    }
    setSimpleSpecs(updatedSpecs);
  };

  const removeSimpleSpec = (index) => {
    setSimpleSpecs(simpleSpecs.filter((_, i) => i !== index));
  };

  const specIcons = {
    Chassis: <RiServerLine />,
    CPU: <RiCpuLine />,
    RAM: <MdOutlineMemory />,
    "LAN adapter": <RiPlugLine />,
    RAID: <RiHardDriveLine />,
    "Power supply unit": <RiPlugLine />,
    "Remote access controller": <RiServerLine />,
    "Rack rails": <RiServerLine />,
    OS: <RiHardDriveLine />,
    "Tray caddy options": <RiHardDriveLine />,
    "Number of CPUs": <RiCpuLine />,
  };

  return (
      <div className="page-content">
        <ToastContainer />
        {isSubmitting && (
            <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ background: "rgba(0, 0, 0, 0.5)", zIndex: 9999 }}
            >
              <div className="text-center bg-white p-4 rounded shadow" style={{ borderRadius: "8px" }}>
                <Spinner color="primary" size="lg" />
                <h5 className="mt-3 text-primary">{t(isUpdateMode ? "ecommerce_add_product.submitting_updating" : "ecommerce_add_product.submitting_creating")}</h5>
              </div>
            </div>
        )}
        <Container fluid>
          <BreadCrumb title={t(isUpdateMode ? "ecommerce_add_product.title_update" : "ecommerce_add_product.title_create")} pageTitle={t("ecommerce_add_product.page_title")} />
          <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
          >
            <Row>
              <Col lg={8}>
                <Card className="shadow-sm" style={{ borderRadius: "8px" }}>
                  <CardHeader className="bg-light">
                    <h5 className="mb-0">{t("ecommerce_add_product.product_info.title")}</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        {t("ecommerce_add_product.product_info.name")}
                      </Label>
                      <Input
                          type="text"
                          id="name"
                          name="name"
                          placeholder={t("ecommerce_add_product.product_info.name_placeholder")}
                          value={validation.values.name}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={validation.touched.name && validation.errors.name}
                          className="form-control"
                          style={{ borderRadius: "6px" }}
                      />
                      {validation.touched.name && validation.errors.name && (
                          <FormFeedback>{validation.errors.name}</FormFeedback>
                      )}
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="brand" className="form-label">
                        {t("ecommerce_add_product.product_info.brand")}
                      </Label>
                      <Input
                          type="text"
                          id="brand"
                          name="brand"
                          placeholder={t("ecommerce_add_product.product_info.brand_placeholder")}
                          value={validation.values.brand}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={validation.touched.brand && validation.errors.brand}
                          className="form-control"
                          style={{ borderRadius: "6px" }}
                      />
                      {validation.touched.brand && validation.errors.brand && (
                          <FormFeedback>{validation.errors.brand}</FormFeedback>
                      )}
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="stock" className="form-label">
                        {t("ecommerce_add_product.product_info.stock")}
                      </Label>
                      <Input
                          type="number"
                          id="stock"
                          name="stock"
                          placeholder={t("ecommerce_add_product.product_info.stock_placeholder")}
                          value={validation.values.stock}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={validation.touched.stock && validation.errors.stock}
                          className="form-control"
                          style={{ borderRadius: "6px" }}
                      />
                      {validation.touched.stock && validation.errors.stock && (
                          <FormFeedback>{validation.errors.stock}</FormFeedback>
                      )}
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="price" className="form-label">
                        {t("ecommerce_add_product.product_info.price")}
                      </Label>
                      <Input
                          type="number"
                          id="price"
                          name="price"
                          placeholder={t("ecommerce_add_product.product_info.price_placeholder")}
                          value={validation.values.price}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={validation.touched.price && validation.errors.price}
                          className="form-control"
                          style={{ borderRadius: "6px" }}
                      />
                      {validation.touched.price && validation.errors.price && (
                          <FormFeedback>{validation.errors.price}</FormFeedback>
                      )}
                    </div>
                    <div className="mb-3">
                      <Label className="form-label">{t("ecommerce_add_product.product_info.description")}</Label>
                      <CKEditor
                          editor={ClassicEditor}
                          data={validation.values.description}
                          onChange={(event, editor) => {
                            validation.setFieldValue("description", editor.getData());
                          }}
                      />
                      {validation.touched.description && validation.errors.description && (
                          <FormFeedback>{validation.errors.description}</FormFeedback>
                      )}
                    </div>
                  </CardBody>
                </Card>

                <Card className="shadow-sm" style={{ borderRadius: "8px" }}>
                  <CardHeader className="bg-light">
                    <h5 className="mb-0">{t("ecommerce_add_product.images.title")}</h5>
                  </CardHeader>
                  <CardBody>
                    <Dropzone onDrop={handleAcceptedFiles}>
                      {({ getRootProps, getInputProps }) => (
                          <div className="dropzone dz-clickable border rounded p-3" style={{ background: "#f8f9fa" }}>
                            <div className="dz-message needsclick" {...getRootProps()}>
                              <div className="mb-3">
                                <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                              </div>
                              <h5>{t("ecommerce_add_product.images.upload")}</h5>
                            </div>
                            <input {...getInputProps()} accept="image/*" />
                          </div>
                      )}
                    </Dropzone>
                    <div>
                      {selectedFiles.map((file, index) => (
                          <Card key={index} className="mt-1 mb-0 shadow-none border">
                            <div className="p-2">
                              <Row className="align-items-center">
                                <Col className="col-auto">
                                  <img
                                      src={file.preview}
                                      className="avatar-sm rounded bg-light"
                                      alt={file.name}
                                  />
                                </Col>
                                <Col>
                                  <p className="text-muted chop font-weight-bold mb-0">{file.name}</p>
                                  <p className="mb-0">
                                    <strong>{file.formattedSize}</strong>
                                  </p>
                                </Col>
                                <Col className="col-auto">
                                  <RiCloseLine
                                      className="cursor-pointer text-danger"
                                      size={18}
                                      onClick={() => deleteImage(index)}
                                  />
                                </Col>
                              </Row>
                            </div>
                          </Card>
                      ))}
                    </div>
                  </CardBody>
                </Card>

                <Card className="shadow-sm" style={{ borderRadius: "8px" }}>
                  <CardHeader className="bg-light">
                    <h5 className="mb-0">{t("ecommerce_add_product.category.title")}</h5>
                  </CardHeader>
                  <CardBody>
                    <Select
                        name="category"
                        options={categories.map((cat) => ({ label: cat.name, value: cat._id }))}
                        onChange={(selected) => {
                          validation.setFieldValue("category", selected ? selected.value : "");
                          setUsedSpecs([]);
                          setSpecConstraints({});
                          setSimpleSpecs([{ key: "", value: "", isNewSpec: false, isNewValue: false }]);
                          setPdfFile(null);
                          setNewProductOption({});
                        }}
                        onBlur={validation.handleBlur}
                        value={categories
                            .map((cat) => ({ label: cat.name, value: cat._id }))
                            .find((option) => option.value === validation.values.category) || null}
                        classNamePrefix="select2-selection"
                        placeholder={t("ecommerce_add_product.category.select")}
                    />
                    {validation.touched.category && validation.errors.category && (
                        <FormFeedback className="d-block">{validation.errors.category}</FormFeedback>
                    )}
                  </CardBody>
                </Card>

                <Card className="shadow-sm" style={{ borderRadius: "8px" }}>
                  <CardHeader className="bg-light">
                    <h5 className="mb-0">{t("ecommerce_add_product.customizable.title")}</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="d-flex mb-3">
                      <div className="form-check me-3">
                        <Input
                            type="radio"
                            name="customizable"
                            id="customizableYes"
                            checked={isCustomizable}
                            onChange={() => setIsCustomizable(true)}
                        />
                        <Label className="form-check-label" for="customizableYes">
                          {t("ecommerce_add_product.customizable.yes")}
                        </Label>
                      </div>
                      <div className="form-check">
                        <Input
                            type="radio"
                            name="customizable"
                            id="customizableNo"
                            checked={!isCustomizable}
                            onChange={() => {
                              setIsCustomizable(false);
                              setPdfFile(null);
                            }}
                        />
                        <Label className="form-check-label" for="customizableNo">
                          {t("ecommerce_add_product.customizable.no")}
                        </Label>
                      </div>
                    </div>
                    {isCustomizable && (
                        <>
                          <Label className="form-label">{t("ecommerce_add_product.customizable.pdf_instructions")}</Label>
                          <Dropzone onDrop={handleAcceptedPdf}>
                            {({ getRootProps, getInputProps }) => (
                                <div className="dropzone dz-clickable border rounded p-3" style={{ background: "#f8f9fa" }}>
                                  <div className="dz-message needsclick" {...getRootProps()}>
                                    <div className="mb-3">
                                      <i className="display-4 text-muted ri-file-pdf-fill" />
                                    </div>
                                    <h5>{t("ecommerce_add_product.customizable.pdf_upload")}</h5>
                                  </div>
                                  <input {...getInputProps()} accept="application/pdf" />
                                </div>
                            )}
                          </Dropzone>
                          {pdfFile && (
                              <Card className="mt-1 mb-0 shadow-none border">
                                <div className="p-2">
                                  <Row className="align-items-center">
                                    <Col className="col-auto">
                                      <i className="ri-file-pdf-fill text-danger" style={{ fontSize: "24px" }} />
                                    </Col>
                                    <Col>
                                      <p className="text-muted chop font-weight-bold mb-0">{pdfFile.name}</p>
                                      <p className="mb-0">
                                        <strong>{pdfFile.formattedSize}</strong>
                                      </p>
                                    </Col>
                                    <Col className="col-auto">
                                      <RiCloseLine
                                          className="cursor-pointer text-danger"
                                          size={18}
                                          onClick={deletePdf}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              </Card>
                          )}
                        </>
                    )}
                  </CardBody>
                </Card>

                {isCustomizable ? (
                    <Card className="shadow-sm" style={{ borderRadius: "8px" }}>
                      <CardHeader className="bg-light">
                        <h5 className="mb-0">{t("ecommerce_add_product.spec_definitions.title")}</h5>
                      </CardHeader>
                      <CardBody>
                        <Alert color="warning">
                          <i className="ri-information-line me-1" /> {t("ecommerce_add_product.spec_definitions.warning")}{" "}
                          <a href="/category_management" className="text-decoration-underline">
                            {t("ecommerce_add_product.spec_definitions.manager_link")}
                          </a>.
                        </Alert>
                        {/* Search Bar */}
                        <Input
                            type="text"
                            placeholder="Search specifications..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="mb-3"
                            style={{ borderRadius: "6px" }}
                        />
                        {filteredSpecs.map((spec) => (
                            <Card key={spec._id} className="mb-2 shadow-sm" style={{ borderRadius: "6px" }}>
                              <CardHeader
                                  style={{ cursor: "pointer", background: "#f8f9fa" }}
                                  onClick={() => togglePanel(spec.name)}
                              >
                                <h6 className="mb-0 d-flex align-items-center">
                                  {specIcons[spec.name] || <RiServerLine className="me-1" />}
                                  {spec.name} {spec.multiple && t("ecommerce_add_product.spec_definitions.multiple")}
                                  <UncontrolledTooltip target={`spec-tooltip-${spec._id}`}>
                                    {t(spec.multiple ? "ecommerce_add_product.spec_definitions.multiple_tooltip" : "ecommerce_add_product.spec_definitions.single_tooltip")}
                                  </UncontrolledTooltip>
                                  <i id={`spec-tooltip-${spec._id}`} className="ri-information-line ms-1" />
                                </h6>
                              </CardHeader>
                              <Collapse isOpen={openPanels[spec.name]}>
                                <CardBody>
                                  {spec.options.length > 0 && (
                                      <div className="row">
                                        {spec.options.map((opt) => (
                                            <div key={opt.name} className="col-6 mb-2">
                                              <span
                                                  className="badge bg-light text-dark"
                                                  style={{
                                                    fontSize: "0.9rem",
                                                    padding: "0.3rem 0.6rem",
                                                    wordBreak: "break-word",
                                                    whiteSpace: "normal",
                                                    maxWidth: "100%",
                                                  }}
                                              >
                                                {opt.name} ({opt.price} DT)
                                              </span>
                                            </div>
                                        ))}
                                      </div>
                                  )}
                                  {spec.rules.length > 0 && (
                                      <div className="mb-2">
                                        <strong>{t("ecommerce_add_product.spec_definitions.rules")}</strong>
                                        <ul className="mb-0">
                                          {spec.rules.map((rule, i) => (
                                              <li key={i} className="text-muted">
                                                {rule.errorMessage}
                                              </li>
                                          ))}
                                        </ul>
                                      </div>
                                  )}
                                  <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => addSpec(spec)}
                                      disabled={usedSpecs.some((s) => s.name === spec.name)}
                                      style={{ borderRadius: "4px" }}
                                  >
                                    {t("ecommerce_add_product.spec_definitions.add_to_product")}
                                  </Button>
                                  <Button
                                      color="secondary"
                                      size="sm"
                                      className="ms-2"
                                      onClick={() => addNewOption(spec.name)}
                                      style={{ borderRadius: "4px" }}
                                  >
                                    {t("ecommerce_add_product.spec_definitions.add_new_option")}
                                  </Button>
                                  {newOption[spec.name] && (
                                      <div className="mt-3 p-3 bg-light rounded">
                                        <Label htmlFor={`new-option-name-${spec.name}`} className="form-label">{t("ecommerce_add_product.spec_definitions.option_name")}</Label>
                                        <Input
                                            type="text"
                                            id={`new-option-name-${spec.name}`}
                                            placeholder={t("ecommerce_add_product.spec_definitions.option_name_placeholder")}
                                            value={newOption[spec.name].name}
                                            onChange={(e) =>
                                                setNewOption({
                                                  ...newOption,
                                                  [spec.name]: { ...newOption[spec.name], name: e.target.value },
                                                })
                                            }
                                            className="mb-2"
                                            style={{ borderRadius: "4px" }}
                                        />
                                        <Label htmlFor={`new-option-price-${spec.name}`} className="form-label">{t("ecommerce_add_product.spec_definitions.option_price")}</Label>
                                        <div className="input-group mb-2">
                                          <Input
                                              type="number"
                                              id={`new-option-price-${spec.name}`}
                                              placeholder={t("ecommerce_add_product.spec_definitions.option_price_placeholder")}
                                              value={newOption[spec.name].price}
                                              onChange={(e) =>
                                                  setNewOption({
                                                    ...newOption,
                                                    [spec.name]: {
                                                      ...newOption[spec.name],
                                                      price: e.target.value ? Number(e.target.value) : 0,
                                                    },
                                                  })
                                              }
                                              style={{ borderRadius: "4px 0 0 4px" }}
                                          />
                                          <span className="input-group-text" style={{ borderRadius: "0 4px 4px 0" }}>
                                            DT
                                          </span>
                                        </div>
                                        {newOptionErrors[spec.name] && (
                                            <Alert color="danger" className="mt-2">{newOptionErrors[spec.name]}</Alert>
                                        )}
                                        <Button
                                            color="success"
                                            size="sm"
                                            onClick={() => saveNewOption(spec._id, spec.name)}
                                            style={{ borderRadius: "4px" }}
                                        >
                                          {t("ecommerce_add_product.spec_definitions.save_option")}
                                        </Button>
                                        <Button
                                            color="light"
                                            size="sm"
                                            className="ms-2"
                                            onClick={() => cancelNewOption(spec.name)}
                                            style={{ borderRadius: "4px" }}
                                        >
                                          {t("ecommerce_add_product.spec_definitions.cancel")}
                                        </Button>
                                        <Alert color="warning" className="mt-2">
                                          <i className="ri-information-line me-1" /> {t("ecommerce_add_product.spec_definitions.update_warning")}
                                        </Alert>
                                      </div>
                                  )}
                                </CardBody>
                              </Collapse>
                            </Card>
                        ))}
                        <Button
                            color="secondary"
                            onClick={addNewSpec}
                            style={{ borderRadius: "4px" }}
                        >
                          {t("ecommerce_add_product.spec_definitions.add_new_spec")}
                        </Button>
                        {newSpec && (
                            <Card className="mt-2 shadow-sm" style={{ borderRadius: "6px" }}>
                              <CardBody className="p-3">
                                <Label htmlFor="new-spec-name" className="form-label">{t("ecommerce_add_product.spec_definitions.spec_name")}</Label>
                                <Input
                                    type="text"
                                    id="new-spec-name"
                                    placeholder={t("ecommerce_add_product.spec_definitions.spec_name_placeholder")}
                                    value={newSpec.name}
                                    onChange={(e) => setNewSpec({ ...newSpec, name: e.target.value })}
                                    className="mb-2"
                                    style={{ borderRadius: "4px" }}
                                />
                                <div className="form-check mb-2">
                                  <Input
                                      type="checkbox"
                                      id="newSpecMultiple"
                                      checked={newSpec.multiple}
                                      onChange={(e) => setNewSpec({ ...newSpec, multiple: e.target.checked })}
                                  />
                                  <Label for="newSpecMultiple" className="form-check-label">
                                    {t("ecommerce_add_product.spec_definitions.allow_multiple")}
                                  </Label>
                                </div>
                                {newSpec.multiple && (
                                    <>
                                      <Label htmlFor="new-spec-max" className="form-label">{t("ecommerce_add_product.spec_definitions.max_selections")}</Label>
                                      <Input
                                          type="number"
                                          id="new-spec-max"
                                          placeholder={t("ecommerce_add_product.spec_definitions.max_selections_placeholder")}
                                          value={newSpec.max}
                                          onChange={(e) => setNewSpec({ ...newSpec, max: Number(e.target.value) })}
                                          className="mb-2"
                                          style={{ borderRadius: "4px" }}
                                      />
                                      {newSpec.options.map((opt, index) => (
                                          <div key={index} className="mb-2">
                                            <Label htmlFor={`new-spec-option-name-${index}`} className="form-label">{t("ecommerce_add_product.spec_definitions.option_name")}</Label>
                                            <Input
                                                type="text"
                                                id={`new-spec-option-name-${index}`}
                                                placeholder={t("ecommerce_add_product.spec_definitions.option_name_placeholder")}
                                                value={opt.name}
                                                onChange={(e) => {
                                                  const updatedOptions = [...newSpec.options];
                                                  updatedOptions[index].name = e.target.value;
                                                  setNewSpec({ ...newSpec, options: updatedOptions });
                                                }}
                                                className="mb-1"
                                                style={{ borderRadius: "4px" }}
                                            />
                                            <Label htmlFor={`new-spec-option-price-${index}`} className="form-label">{t("ecommerce_add_product.spec_definitions.option_price")}</Label>
                                            <div className="input-group">
                                              <Input
                                                  type="number"
                                                  id={`new-spec-option-price-${index}`}
                                                  placeholder={t("ecommerce_add_product.spec_definitions.option_price_placeholder")}
                                                  value={opt.price}
                                                  onChange={(e) => {
                                                    const updatedOptions = [...newSpec.options];
                                                    updatedOptions[index].price = e.target.value ? Number(e.target.value) : 0;
                                                    setNewSpec({ ...newSpec, options: updatedOptions });
                                                  }}
                                                  style={{ borderRadius: "4px 0 0 4px" }}
                                              />
                                              <span className="input-group-text" style={{ borderRadius: "0 4px 4px 0" }}>
                                                DT
                                              </span>
                                            </div>
                                          </div>
                                      ))}
                                      <Button
                                          color="light"
                                          size="sm"
                                          onClick={() =>
                                              setNewSpec({
                                                ...newSpec,
                                                options: [...newSpec.options, { name: "", price: 0 }],
                                              })
                                          }
                                          style={{ borderRadius: "4px" }}
                                      >
                                        {t("ecommerce_add_product.spec_definitions.add_option")}
                                      </Button>
                                    </>
                                )}
                                {!newSpec.multiple && (
                                    <div className="mb-2">
                                      <Label htmlFor="new-spec-base-price" className="form-label">{t("ecommerce_add_product.spec_definitions.base_price")}</Label>
                                      <div className="input-group">
                                        <Input
                                            type="number"
                                            id="new-spec-base-price"
                                            placeholder={t("ecommerce_add_product.spec_definitions.base_price_placeholder")}
                                            value={newSpec.basePrice || ""}
                                            onChange={(e) =>
                                                setNewSpec({ ...newSpec, basePrice: e.target.value ? Number(e.target.value) : null })
                                            }
                                            style={{ borderRadius: "4px 0 0 4px" }}
                                        />
                                        <span className="input-group-text" style={{ borderRadius: "0 4px 4px 0" }}>
                                          DT
                                        </span>
                                      </div>
                                    </div>
                                )}
                                {newSpecError && <Alert color="danger" className="mt-2">{newSpecError}</Alert>}
                                <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => saveNewSpec(validation.values.category)}
                                    style={{ borderRadius: "4px" }}
                                >
                                  {t("ecommerce_add_product.spec_definitions.save_spec")}
                                </Button>
                                <Button
                                    color="light"
                                    size="sm"
                                    className="ms-2"
                                    onClick={cancelNewSpec}
                                    style={{ borderRadius: "4px" }}
                                >
                                  {t("ecommerce_add_product.spec_definitions.cancel")}
                                </Button>
                                <Alert color="warning" className="mt-2">
                                  <i className="ri-information-line me-1" /> {t("ecommerce_add_product.spec_definitions.update_warning")}
                                </Alert>
                              </CardBody>
                            </Card>
                        )}
                      </CardBody>
                    </Card>
                ) : (
                    <Card className="shadow-sm" style={{ borderRadius: "8px" }}>
                      <CardHeader className="bg-light">
                        <h5 className="mb-0">{t("ecommerce_add_product.specifications.title")}</h5>
                      </CardHeader>
                      <CardBody>
                        <Alert color="info" className="mb-3">
                          <i className="ri-information-line me-1" /> {t("ecommerce_add_product.specifications.info")}
                        </Alert>
                        {simpleSpecs.map((spec, index) => (
                            <div key={index} className="mb-3 d-flex align-items-center">
                              <div className="flex-grow-1 me-2">
                                <Label htmlFor={`spec-key-${index}`} className="form-label">{t("ecommerce_add_product.specifications.spec_name")}</Label>
                                {spec.isNewSpec ? (
                                    <Input
                                        type="text"
                                        id={`spec-key-${index}`}
                                        placeholder={t("ecommerce_add_product.specifications.spec_name_placeholder")}
                                        value={spec.key}
                                        onChange={(e) => updateSimpleSpec(index, "key", e.target.value)}
                                        style={{ borderRadius: "4px" }}
                                    />
                                ) : (
                                    <Select
                                        id={`spec-key-${index}`}
                                        options={[
                                          ...(possibleSpecs || []).map((ps) => ({ label: ps.name, value: ps.name })),
                                          { label: t("ecommerce_add_product.specifications.add_new_spec"), value: "__new__" }
                                        ]}
                                        onChange={(selected) => {
                                          if (selected.value === "__new__") {
                                            updateSimpleSpec(index, "isNewSpec", true);
                                            updateSimpleSpec(index, "key", "");
                                            updateSimpleSpec(index, "value", "");
                                            updateSimpleSpec(index, "isNewValue", false);
                                          } else {
                                            updateSimpleSpec(index, "isNewSpec", false);
                                            updateSimpleSpec(index, "key", selected.value);
                                            updateSimpleSpec(index, "value", "");
                                            updateSimpleSpec(index, "isNewValue", false);
                                          }
                                        }}
                                        value={
                                          spec.key && possibleSpecs.some((ps) => ps.name === spec.key)
                                              ? { label: spec.key, value: spec.key }
                                              : null
                                        }
                                        classNamePrefix="select2-selection"
                                        placeholder={t("ecommerce_add_product.specifications.select_spec")}
                                    />
                                )}
                                <Label htmlFor={`spec-value-${index}`} className="form-label mt-2">{t("ecommerce_add_product.specifications.spec_value")}</Label>
                                {spec.key && !spec.isNewSpec && !spec.isNewValue && possibleSpecs.find((ps) => ps.name === spec.key)?.options?.length > 0 ? (
                                    <Select
                                        id={`spec-value-${index}`}
                                        options={[
                                          ...(possibleSpecs.find((ps) => ps.name === spec.key)?.options || []).map((opt) => ({
                                            label: opt.value,
                                            value: opt.value,
                                          })),
                                          { label: t("ecommerce_add_product.specifications.add_new_value"), value: "__new__" },
                                        ]}
                                        onChange={(selected) => {
                                          if (selected.value === "__new__") {
                                            updateSimpleSpec(index, "isNewValue", true);
                                            updateSimpleSpec(index, "value", "");
                                          } else {
                                            updateSimpleSpec(index, "isNewValue", false);
                                            updateSimpleSpec(index, "value", selected.value);
                                          }
                                        }}
                                        value={
                                          spec.value && possibleSpecs.find((ps) => ps.name === spec.key)?.options.some((opt) => opt.value === spec.value)
                                              ? { label: spec.value, value: spec.value }
                                              : null
                                        }
                                        classNamePrefix="select2-selection"
                                        placeholder={t("ecommerce_add_product.specifications.select_value")}
                                    />
                                ) : (
                                    <Input
                                        type="text"
                                        id={`spec-value-${index}`}
                                        placeholder={t("ecommerce_add_product.specifications.spec_value_placeholder")}
                                        value={spec.value}
                                        onChange={(e) => updateSimpleSpec(index, "value", e.target.value)}
                                        style={{ borderRadius: "4px" }}
                                    />
                                )}
                              </div>
                              <RiCloseLine
                                  className="cursor-pointer text-danger"
                                  size={18}
                                  onClick={() => removeSimpleSpec(index)}
                              />
                            </div>
                        ))}
                        <Button
                            color="secondary"
                            size="sm"
                            onClick={addSimpleSpec}
                            style={{ borderRadius: "4px" }}
                        >
                          {t("ecommerce_add_product.specifications.add_spec")}
                        </Button>
                      </CardBody>
                    </Card>
                )}

                <div className="text-end mb-3">
                  <Button
                      type="submit"
                      color="success"
                      disabled={isSubmitting}
                      style={{ borderRadius: "4px", opacity: isSubmitting ? 0.6 : 1 }}
                  >
                    {t(isUpdateMode ? "ecommerce_add_product.actions.update" : "ecommerce_add_product.actions.submit")}
                  </Button>
                </div>
              </Col>

              {isCustomizable && (
                  <Col lg={4}>
                    <Card
                        className="shadow-sm"
                        style={{
                          borderRadius: "8px",
                          position: "sticky",
                          top: "20px",
                          border: "1px solid #e9ecef",
                          background: "#f8f9fa",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                        }}
                    >
                      <CardHeader className="bg-light">
                        <h5 className="mb-0">{t("ecommerce_add_product.selected_specs.title")}</h5>
                      </CardHeader>
                      <CardBody className="p-3">
                        {usedSpecs.length === 0 ? (
                            <p className="text-muted">{t("ecommerce_add_product.selected_specs.no_specs")}</p>
                        ) : (
                            usedSpecs.map((spec) => (
                                <div key={spec.name} className="mb-3">
                                  <div className="d-flex align-items-center">
                                    <h6 className="mb-0 d-flex align-items-center" style={{ fontSize: "1rem" }}>
                                      {specIcons[spec.name] || <RiServerLine className="me-1" />}
                                      {spec.name}
                                    </h6>
                                    <RiCloseLine
                                        className="ms-auto cursor-pointer text-danger"
                                        size={18}
                                        onClick={() => removeSpec(spec.name)}
                                    />
                                  </div>
                                  <ul className="list-unstyled mt-2">
                                    {spec.options.map((opt) => {
                                      const specDef = specDefinitions.find((s) => s.name === spec.name);
                                      const optionPrice = opt.price !== undefined
                                          ? opt.included ? 0 : opt.price
                                          : opt.included ? 0 : specDef?.options.find((o) => o.name === opt.name)?.price || 0;
                                      return (
                                          <li key={opt.name} className="d-flex align-items-center mb-1">
                                            <span
                                                className="badge bg-light text-dark me-2"
                                                style={{
                                                  fontSize: "0.9rem",
                                                  padding: "0.3rem 0.6rem",
                                                  wordBreak: "break-word",
                                                  whiteSpace: "normal",
                                                  maxWidth: "150px"
                                                }}
                                            >
                                              {opt.name} ({optionPrice} DT)
                                            </span>
                                            <div className="form-check me-2">
                                              <Input
                                                  type="checkbox"
                                                  id={`included-${spec.name}-${opt.name}`}
                                                  checked={opt.included}
                                                  onChange={() => toggleIncluded(spec.name, opt.name)}
                                              />
                                              <Label
                                                  for={`included-${spec.name}-${opt.name}`}
                                                  className="form-check-label"
                                                  style={{ fontSize: "0.9rem" }}
                                              >
                                                {t("ecommerce_add_product.selected_specs.included")}
                                              </Label>
                                            </div>
                                            <RiCloseLine
                                                className="cursor-pointer text-danger"
                                                size={16}
                                                onClick={() => removeOption(spec.name, opt.name)}
                                            />
                                          </li>
                                      );
                                    })}
                                  </ul>
                                  <Button
                                      color="secondary"
                                      size="sm"
                                      className="mt-2"
                                      onClick={() => addProductOption(spec.name)}
                                      style={{ borderRadius: "4px" }}
                                  >
                                    {t("ecommerce_add_product.selected_specs.add_product_option")}
                                  </Button>
                                  {newProductOption[spec.name] && (
                                      <div className="mt-3 p-3 bg-light rounded">
                                        <Label htmlFor={`new-product-option-name-${spec.name}`} className="form-label">{t("ecommerce_add_product.selected_specs.option_name")}</Label>
                                        <Input
                                            type="text"
                                            id={`new-product-option-name-${spec.name}`}
                                            placeholder={t("ecommerce_add_product.selected_specs.option_name_placeholder")}
                                            value={newProductOption[spec.name].name}
                                            onChange={(e) =>
                                                setNewProductOption({
                                                  ...newProductOption,
                                                  [spec.name]: { ...newProductOption[spec.name], name: e.target.value }
                                                })
                                            }
                                            className="mb-2"
                                            style={{ borderRadius: "4px" }}
                                        />
                                        <Label htmlFor={`new-product-option-price-${spec.name}`} className="form-label">{t("ecommerce_add_product.selected_specs.option_price")}</Label>
                                        <div className="input-group mb-2">
                                          <Input
                                              type="number"
                                              id={`new-product-option-price-${spec.name}`}
                                              placeholder={t("ecommerce_add_product.selected_specs.option_price_placeholder")}
                                              value={newProductOption[spec.name].price}
                                              onChange={(e) =>
                                                  setNewProductOption({
                                                    ...newProductOption,
                                                    [spec.name]: {
                                                      ...newProductOption[spec.name],
                                                      price: e.target.value ? Number(e.target.value) : 0
                                                    }
                                                  })
                                              }
                                              style={{ borderRadius: "4px 0 0 4px" }}
                                          />
                                          <span className="input-group-text" style={{ borderRadius: "0 4px 4px 0" }}>
                                            DT
                                          </span>
                                        </div>
                                        <Button
                                            color="success"
                                            size="sm"
                                            onClick={() => saveProductOption(spec.name)}
                                            style={{ borderRadius: "4px" }}
                                        >
                                          {t("ecommerce_add_product.selected_specs.save_option")}
                                        </Button>
                                        <Button
                                            color="light"
                                            size="sm"
                                            className="ms-2"
                                            onClick={() => cancelProductOption(spec.name)}
                                            style={{ borderRadius: "4px" }}
                                        >
                                          {t("ecommerce_add_product.selected_specs.cancel")}
                                        </Button>
                                        <Alert color="info" className="mt-2">
                                          <i className="ri-information-line me-1" /> {t("ecommerce_add_product.selected_specs.option_info")}
                                        </Alert>
                                      </div>
                                  )}
                                  {specDefinitions.find((s) => s.name === spec.name)?.multiple && (
                                      <div className="mt-2">
                                        <Label htmlFor={`max-items-${spec.name}`} className="form-label">{t("ecommerce_add_product.selected_specs.max_items")}</Label>
                                        <Input
                                            type="number"
                                            id={`max-items-${spec.name}`}
                                            placeholder={t("ecommerce_add_product.selected_specs.max_items_placeholder")}
                                            value={specConstraints[spec.name]?.maxItems || ""}
                                            onChange={(e) => updateConstraint(spec.name, "maxItems", e.target.value)}
                                            style={{ borderRadius: "4px" }}
                                        />
                                        <div className="form-check mt-2">
                                          <Input
                                              type="checkbox"
                                              id={`maxTotal-${spec.name}`}
                                              checked={specConstraints[spec.name]?.maxTotal !== undefined}
                                              onChange={(e) =>
                                                  updateConstraint(spec.name, "maxTotal", e.target.checked ? 0 : undefined)
                                              }
                                          />
                                          <Label for={`maxTotal-${spec.name}`} className="form-check-label">
                                            {t("ecommerce_add_product.selected_specs.set_max_total")}
                                          </Label>
                                        </div>
                                        {specConstraints[spec.name]?.maxTotal !== undefined && (
                                            <div className="input-group mt-2">
                                              <Input
                                                  type="number"
                                                  id={`max-total-${spec.name}`}
                                                  placeholder={t("ecommerce_add_product.selected_specs.max_total_placeholder")}
                                                  value={specConstraints[spec.name]?.maxTotal || ""}
                                                  onChange={(e) => updateConstraint(spec.name, "maxTotal", e.target.value)}
                                                  style={{ borderRadius: "4px 0 0 4px" }}
                                              />
                                              <span className="input-group-text" style={{ borderRadius: "0 4px 4px 0" }}>
                                                {spec.name === "RAM" ? "GB" : "DT"}
                                              </span>
                                            </div>
                                        )}
                                      </div>
                                  )}
                                </div>
                            ))
                        )}
                      </CardBody>
                    </Card>
                  </Col>
              )}
            </Row>
          </Form>
        </Container>
      </div>
  );
};

export default EcommerceAddProduct;